import { useEffect, useLayoutEffect, useRef } from "react"
import { motion } from "motion/react"
import type { DamageZone } from "../../types"
import { walkLine } from "../capture-copy"
import { StandMap } from "../stand-map"
import type { StandPoint } from "../car-plan"
import type { PlannedShot } from "../types"

// Between shots: the camera dims and a top-down car shows a dot walking from
// where they stood to where they need to stand next. Gone by itself in a
// moment; a tap skips it.

const SHOW_MS = 1700

export function WalkCard({
  shot,
  plan,
  damage,
  from,
  onDone,
}: {
  shot: PlannedShot
  plan: readonly PlannedShot[]
  damage: readonly DamageZone[]
  from: StandPoint | null
  onDone: () => void
}) {
  const done = useRef(onDone)
  useLayoutEffect(() => {
    done.current = onDone
  })
  useEffect(() => {
    const t = setTimeout(() => done.current(), SHOW_MS)
    return () => clearTimeout(t)
  }, [shot.id])

  return (
    <motion.button
      type="button"
      onClick={() => done.current()}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/78 px-8 text-center backdrop-blur-[2px]"
      aria-label={`${walkLine(shot)}. Tap to continue.`}
    >
      <div className="w-[150px]">
        <StandMap shot={shot} plan={plan} damage={damage} from={from} walk tone="dark" />
      </div>
      <p className="mt-6 text-[22px] font-semibold leading-snug text-white">{walkLine(shot)}</p>
      <p className="mt-3 text-[14px] text-white/60">Tap to continue</p>
    </motion.button>
  )
}
